import { GOOGLE_CLIENT_ID } from "@/constants/appConstants";
import { UserProfile } from "@/types/userDetails";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";

interface GoogleAuthResponse {
  clientId: string;
  callback: (response: any) => void;
}

interface GoogleAuth {
  id: {
    initialize: (config: GoogleAuthResponse) => void;
    renderButton: (container: HTMLElement, options: any) => void;
    prompt: () => void;
  };
  [key: string]: any; // Fallback for any other properties that might be present
}

declare global {
  interface Window {
    google: GoogleAuth;
  }
}

type GoogleCredentialResponse = {
  credential: string;
};

const loadGoogleSDK = () => {
  (function (document, scriptTag, sdkId) {
    const firstScriptElement = document.getElementsByTagName(scriptTag)[0];
    if (document.getElementById(sdkId)) return;
    const googleScript = document.createElement(scriptTag) as HTMLScriptElement;
    googleScript.id = sdkId;
    googleScript.src = "https://accounts.google.com/gsi/client";
    if (firstScriptElement && firstScriptElement.parentNode) {
      firstScriptElement.parentNode.insertBefore(
        googleScript,
        firstScriptElement,
      );
    }
  })(document, "script", "google-jssdk");
};

const useGoogleAuthentication = () => {
  let countLoad = 0;
  const maxRetry = 20;
  const retryInterval = 200; // milliseconds

  const [isGoogleLoading, setIsGoogleLoading] = useState(true);
  const [googleUser, setGoogleUser] = useState<UserProfile | null>(null);

  const waitForGoogleLibrary = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const checkGoogle = () => {
        if (typeof window.google !== "undefined" && window.google.accounts) {
          setIsGoogleLoading(false);
          resolve();
        } else if (countLoad < maxRetry) {
          countLoad++;
          setTimeout(checkGoogle, retryInterval);
        } else {
          reject(
            new Error("Google Identity Services not available after retries"),
          );
        }
      };
      checkGoogle();
    });
  };

  const initializeGsi = async () => {
    try {
      await waitForGoogleLibrary();
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });

      window.google.accounts.id.prompt();
    } catch (error) {
      let _configObj = {
        provider: "google",
        error: true,
      };
      setGoogleUser(_configObj);
      console.error("Failed to initialize Google Identity Services:", error);
    }
  };

  const createFakeGoogleWrapper = () => {
    const googleLoginWrapper = document.createElement("div");
    // Or you can simple hide it in CSS rule for custom-google-button
    googleLoginWrapper.style.display = "none";
    googleLoginWrapper.classList.add("custom-google-button");

    // Add the wrapper to body
    document.body.appendChild(googleLoginWrapper);

    // Use GSI javascript api to render the button inside our wrapper
    // You can ignore the properties because this button will not appear
    window.google.accounts.id.renderButton(googleLoginWrapper, {
      type: "icon",
      width: "200",
    });

    const googleLoginWrapperButton =
      googleLoginWrapper.querySelector("div[role=button]");

    return {
      click: () => {
        (googleLoginWrapperButton as HTMLElement).click();
      },
    };
  };

  const handleCredentialResponse = (response: GoogleCredentialResponse) => {
    let _configObj = {
      provider: "google",
      error: false,
    };
    let userObject: UserProfile = jwtDecode(response.credential);
    let { name, email, email_verified } = userObject;
    setGoogleUser({
      name,
      email,
      email_verified,
      accessToken: response.credential,
      ..._configObj,
    });
  };

  const handleGoogleBtnClick = () => {
    const googleButtonWrapper = createFakeGoogleWrapper();
    googleButtonWrapper.click();
  };

  useEffect(() => {
    // Initialize Google Identity Services
    loadGoogleSDK();
    initializeGsi();
  }, []);

  return {
    isGoogleLoading,
    handleGoogleBtnClick,
    googleUser,
  };
};

export default useGoogleAuthentication;
